import React from 'react';
import type { AnnotationShape } from '../types';
import { RectShape } from './shapes/RectShape';
import { EllipseShape } from './shapes/EllipseShape';
import { LineShape } from './shapes/LineShape';
import { ArrowShape } from './shapes/ArrowShape';
import { FreeformShape } from './shapes/FreeformShape';
import { TextShape } from './shapes/TextShape';
import { getShapeBounds } from '../hooks/useShapeResize';
import { computeRotationTransform } from '../utils/geometry';
import { describeShape } from '../utils/createDefaultShape';

interface SvgCanvasProps {
  shapes: AnnotationShape[];
  drawingShape?: AnnotationShape | null;
  isEditor?: boolean;
  selectedIds?: Set<string>;
  containerWidth?: number;
  containerHeight?: number;
  /**
   * When set, shapes use this strokeWidth in viewBox units instead of
   * the fixed 2px non-scaling-stroke. Used by the annotation viewer so
   * strokes scale proportionally with the container rather than staying
   * at a constant screen-pixel width.
   */
  scaledStrokeWidth?: number;
  /** Optional descriptive aria-label override. */
  ariaLabel?: string;
  /** Called when a shape `<g>` receives focus (editor mode only). */
  onShapeFocus?: (id: string) => void;
  /** Called when Enter is pressed on a focused shape (editor mode only). */
  onShapeActivate?: (id: string) => void;
  children?: React.ReactNode;
  onPointerDown?: (e: React.PointerEvent<SVGSVGElement>) => void;
  onPointerMove?: (e: React.PointerEvent<SVGSVGElement>) => void;
  onPointerUp?: (e: React.PointerEvent<SVGSVGElement>) => void;
  onPointerLeave?: (e: React.PointerEvent<SVGSVGElement>) => void;
}

/**
 * For line/arrow shapes with rotation, compute rotated endpoints directly in screen space
 * to avoid SVG transform blur artifacts with vectorEffect="non-scaling-stroke".
 */
function rotateLineEndpoints<T extends AnnotationShape & { type: 'line' | 'arrow' }>(
  shape: T,
  rotation: number,
  containerWidth: number,
  containerHeight: number,
): T {
  const bounds = getShapeBounds(shape);
  const cxNorm = bounds.x + bounds.width / 2;
  const cyNorm = bounds.y + bounds.height / 2;

  // Rotate in screen space then convert back
  const rad = (rotation * Math.PI) / 180;
  const cos = Math.cos(rad);
  const sin = Math.sin(rad);

  function rotPt(nx: number, ny: number): { x: number; y: number } {
    const sx = nx * containerWidth;
    const sy = ny * containerHeight;
    const scx = cxNorm * containerWidth;
    const scy = cyNorm * containerHeight;
    const dx = sx - scx;
    const dy = sy - scy;
    return {
      x: (scx + dx * cos - dy * sin) / containerWidth,
      y: (scy + dx * sin + dy * cos) / containerHeight,
    };
  }

  const p1 = rotPt(shape.x1, shape.y1);
  const p2 = rotPt(shape.x2, shape.y2);

  return { ...shape, x1: p1.x, y1: p1.y, x2: p2.x, y2: p2.y, rotation: 0 };
}

interface RenderShapeOptions {
  isEditor?: boolean;
  isSelected?: boolean;
  containerWidth?: number;
  containerHeight?: number;
  scaledStrokeWidth?: number;
  onShapeFocus?: (id: string) => void;
  onShapeActivate?: (id: string) => void;
}

function renderShape(
  shape: AnnotationShape,
  opts: RenderShapeOptions = {},
) {
  const { isEditor, isSelected, containerWidth, containerHeight, scaledStrokeWidth, onShapeFocus, onShapeActivate } = opts;
  const ssw = scaledStrokeWidth;
  // For lines/arrows with rotation, compute rotated endpoints directly (avoids blur)
  let element: React.ReactNode;
  if (shape.type === 'line' && shape.rotation && containerWidth && containerHeight) {
    const rotated = rotateLineEndpoints(shape, shape.rotation, containerWidth, containerHeight);
    element = <LineShape shape={rotated} isEditor={isEditor} isSelected={isSelected} scaledStrokeWidth={ssw} />;
    return wrapFocusable(shape, element, opts);
  }
  if (shape.type === 'arrow' && shape.rotation && containerWidth && containerHeight) {
    const rotated = rotateLineEndpoints(shape, shape.rotation, containerWidth, containerHeight);
    element = (
      <ArrowShape
        shape={rotated}
        isEditor={isEditor}
        isSelected={isSelected}
        containerWidth={containerWidth}
        containerHeight={containerHeight}
        scaledStrokeWidth={ssw}
      />
    );
    return wrapFocusable(shape, element, opts);
  }

  switch (shape.type) {
    case 'box':
      element = <RectShape shape={shape} isEditor={isEditor} isSelected={isSelected} scaledStrokeWidth={ssw} />;
      break;
    case 'circle':
      element = <EllipseShape shape={shape} isEditor={isEditor} isSelected={isSelected} scaledStrokeWidth={ssw} />;
      break;
    case 'line':
      element = <LineShape shape={shape} isEditor={isEditor} isSelected={isSelected} scaledStrokeWidth={ssw} />;
      break;
    case 'arrow':
      element = (
        <ArrowShape
          shape={shape}
          isEditor={isEditor}
          isSelected={isSelected}
          containerWidth={containerWidth}
          containerHeight={containerHeight}
          scaledStrokeWidth={ssw}
        />
      );
      break;
    case 'freeform':
      element = <FreeformShape shape={shape} isEditor={isEditor} isSelected={isSelected} scaledStrokeWidth={ssw} />;
      break;
    case 'text':
      element = <TextShape shape={shape} isEditor={isEditor} isSelected={isSelected} scaledStrokeWidth={ssw} />;
      break;
    default:
      return null;
  }

  // Wrap in rotation transform if shape has rotation
  if (shape.rotation && containerWidth && containerHeight) {
    const bounds = getShapeBounds(shape);
    const cx = bounds.x + bounds.width / 2;
    const cy = bounds.y + bounds.height / 2;
    const transform = computeRotationTransform(shape.rotation, cx, cy, containerWidth, containerHeight);
    element = <g transform={transform}>{element}</g>;
  }

  return wrapFocusable(shape, element, opts);
}

/**
 * In editor mode, wrap each shape in a focusable `<g>` so Tab cycles through
 * shapes natively. Each group exposes role="img" + a descriptive aria-label
 * and reports focus/Enter back to the parent. In viewer mode, just return the
 * element unwrapped.
 */
function wrapFocusable(
  shape: AnnotationShape,
  element: React.ReactNode,
  opts: RenderShapeOptions,
): React.ReactNode {
  const { isEditor, isSelected, onShapeFocus, onShapeActivate } = opts;
  if (!isEditor) {
    return <g key={shape.id}>{element}</g>;
  }
  return (
    <g
      key={shape.id}
      tabIndex={0}
      role="img"
      aria-label={describeShape(shape)}
      aria-selected={isSelected ? 'true' : undefined}
      onFocus={() => onShapeFocus?.(shape.id)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.stopPropagation();
          onShapeActivate?.(shape.id);
        }
      }}
      style={{ outline: 'none' }}
    >
      {element}
    </g>
  );
}

export const SvgCanvas = React.memo(React.forwardRef<SVGSVGElement, SvgCanvasProps>(function SvgCanvas({
  shapes,
  drawingShape,
  isEditor,
  selectedIds,
  containerWidth,
  containerHeight,
  scaledStrokeWidth,
  ariaLabel,
  onShapeFocus,
  onShapeActivate,
  children,
  onPointerDown,
  onPointerMove,
  onPointerUp,
  onPointerLeave,
}, ref) {
  return (
    <svg
      ref={ref}
      className="kollab-annotation-canvas"
      viewBox="0 0 1 1"
      preserveAspectRatio="none"
      overflow="hidden"
      role={isEditor ? 'application' : 'img'}
      aria-label={ariaLabel ?? (isEditor ? 'Annotation drawing canvas' : 'Annotations')}
      tabIndex={isEditor ? 0 : undefined}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerLeave={onPointerLeave}
    >
      {shapes.map((shape) => renderShape(shape, {
        isEditor, isSelected: selectedIds?.has(shape.id), containerWidth, containerHeight, scaledStrokeWidth, onShapeFocus, onShapeActivate,
      }))}
      {drawingShape && renderShape(drawingShape, { isEditor, isSelected: false, containerWidth, containerHeight, scaledStrokeWidth })}
      {children}
    </svg>
  );
}));
